// Agendamiento · ficha de una reserva (drawer, mismo formato que la ficha de usuario).
//
// Lo que se puede hacer depende del estado:
//   · confirmada → cambiar hora, registrar asistencia, reenviar correo, cancelar.
//   · pagada sin hora → elegir hora, reenviar correo, cancelar.
//   · en pago → consultar a Transbank ahora.
//   · boleta incierta o con error → resolver después de mirar en Bsale.
// Cancelar NO devuelve plata ni emite nota de crédito: eso lo gestiona finanzas.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, App, Checkbox, Drawer, Input, InputNumber, Modal, Radio, Spin, Tabs } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import {
  FiCalendar, FiCheck, FiClock, FiCreditCard, FiFileText, FiMail, FiMapPin, FiPhone, FiRefreshCw,
  FiSlash, FiTag, FiUser, FiX, FiXCircle,
} from 'react-icons/fi';
import Agenda from '../../../../service/Agenda';
import { mensajeDeError } from '../../../../service/client';
import { useNotify } from '../../../../providers/NotificationProvider';
import useAuthContext from '../../../../hooks/useAuthContext';
import { can } from '../../../../config/rbac';
import { Iniciales, claseBtnPrimario, claseBtnSecundario } from '../../../Ui/Admin/PiezasAdmin';
import { Chip, Dato, Etiqueta, chipDe } from './piezas';
import ReprogramarModal from './ReprogramarModal';
import {
  ACCIONES_HISTORIAL, BOLETA, ESTADOS, RESULTADO_PAGO, TARIFA, TIPO_TARJETA, VECINO_PADRON, fechaHora, fechaLarga, hora, pesos,
} from './util';

// Para el indicador de la ficha: una o dos palabras que quepan en la tarjeta.
const BOLETA_CORTA = { no_aplica: '—', pendiente: 'Pendiente', emitiendo: 'Emitiendo', error: 'Con error', incierta: 'Revisar' };

const claseBtnPeligro =
  'inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-red-200 bg-white text-red-600 text-sm font-semibold hover:bg-red-50 transition-colors disabled:opacity-50 disabled:pointer-events-none';

export default function DetalleReserva({ citaId, accionInicial, onClose, onCambio }) {
  const notify = useNotify();
  const { modal } = App.useApp();
  const { user } = useAuthContext();
  const puedeEditar = can(user, 'agenda', 'editar');
  const angosto = typeof window !== 'undefined' && window.innerWidth < 900;

  const [cita, setCita] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [reprogramar, setReprogramar] = useState(false);
  const [cancelar, setCancelar] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [devolucion, setDevolucion] = useState(false);
  const [notaDevolucion, setNotaDevolucion] = useState('');
  const [boleta, setBoleta] = useState({ abierta: false, decision: 'existe', numero: null, url: '' });
  const accionHecha = useRef(null);

  const cargar = useCallback(async () => {
    if (!citaId) return;
    setCargando(true);
    try {
      setCita(await Agenda.cita(citaId));
    } catch (err) {
      notify.error(mensajeDeError(err, 'No se pudo cargar la reserva'));
    } finally {
      setCargando(false);
    }
  }, [citaId, notify]);

  useEffect(() => { setCita(null); accionHecha.current = null; cargar(); }, [cargar]);

  const accion = async (fn, exito) => {
    setOcupado(true);
    try {
      await fn();
      if (exito) notify.success(exito);
      await cargar();
      onCambio?.();
      return true;
    } catch (err) {
      notify.error(mensajeDeError(err, 'No se pudo completar la acción'));
      return false;
    } finally {
      setOcupado(false);
    }
  };

  const reenviar = () => modal.confirm({
    title: '¿Reenviar el correo de confirmación?',
    content: `Se envía a ${cita.cliente?.email} en los próximos minutos.`,
    okText: 'Reenviar', cancelText: 'Volver',
    onOk: () => accion(() => Agenda.reenviarCorreo(cita._id), 'Correo encolado. Sale en los próximos minutos.'),
  });

  // Acción pedida desde el menú de la tabla: se abre apenas carga la ficha.
  useEffect(() => {
    if (!cita || !accionInicial || accionHecha.current === cita._id) return;
    accionHecha.current = cita._id;
    if (accionInicial === 'hora') setReprogramar(true);
    if (accionInicial === 'cancelar') setCancelar(true);
    if (accionInicial === 'correo') reenviar();
  }, [cita, accionInicial]); // eslint-disable-line react-hooks/exhaustive-deps

  const e = cita?.estado;
  const pagada = e === 'confirmada' || e === 'pagada_sin_hora';
  const empezo = cita?.inicio && new Date(cita.inicio).getTime() - Date.now() <= 60 * 60_000;
  const boletaPorResolver = ['incierta', 'error'].includes(cita?.facturacion?.estado);
  const doc = cita?.facturacion?.documento;

  return (
    <Drawer
      open={Boolean(citaId)} onClose={onClose} width={angosto ? '100%' : 880} destroyOnHidden
      closable={false} title={null}
      styles={{ header: { display: 'none' }, body: { padding: 0, background: '#f4f6f9' } }}
    >
      {/* ── Cabecera ── */}
      <header className="relative bg-[#00266b] text-white px-6 pt-6 pb-5 overflow-hidden">
        <div aria-hidden className="absolute inset-0 opacity-[.08] pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '22px 22px',
        }} />
        <button type="button" onClick={onClose} aria-label="Cerrar ficha"
          className="absolute top-4 right-4 w-9 h-9 inline-flex items-center justify-center rounded-full text-blue-100 hover:bg-white/10 hover:text-white transition-colors">
          <FiX size={18} />
        </button>
        <div className="relative flex items-center gap-4 pr-10">
          <Iniciales nombre={cita?.cliente?.nombre} className="!w-14 !h-14 !text-lg !bg-white !text-primary ring-4 ring-white/15" />
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-extrabold text-white leading-tight m-0 truncate">
              {cita?.cliente?.nombre || 'Reserva'}
            </h2>
            <p className="text-sm text-blue-100 m-0 mt-0.5 truncate">
              {cita?.servicioNombre}{cita?.codigo && <span className="text-blue-200"> · {cita.codigo}</span>}
            </p>
            {cita && (
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-white/15 text-white">
                  <span className={`w-1.5 h-1.5 rounded-full ${e === 'confirmada' ? 'bg-secondary' : 'bg-blue-300'}`} /> {ESTADOS[e]?.[1] || e}
                </span>
                {cita.asistencia && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-white/10 text-blue-100">
                    {cita.asistencia === 'asistio' ? 'Asistió' : 'No asistió'}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {cita && (
          <div className="relative mt-5 flex items-end justify-between gap-4 pt-4 border-t border-white/10">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-secondary m-0">Hora</p>
              <p className="text-2xl font-extrabold m-0 tabular-nums">
                {cita.inicio ? `${hora(cita.inicio)}–${hora(cita.fin)}` : 'Sin hora'}
              </p>
              {cita.inicio && <p className="text-sm text-blue-100 m-0 capitalize">{fechaLarga(cita.inicio)}</p>}
            </div>
            <div className="text-right">
              <p className="text-[11px] font-bold uppercase tracking-widest text-secondary m-0">Pagado</p>
              <p className="text-2xl font-extrabold m-0 tabular-nums">
                {cita.pago?.resultado === 'aprobado' ? pesos(cita.pago.monto) : '—'}
              </p>
            </div>
          </div>
        )}
      </header>

      <div className="p-5 sm:p-6 space-y-5">
        {cargando && !cita && <div className="py-16 text-center"><Spin size="large" /></div>}

        {cita && (
          <>
            {e === 'pagada_sin_hora' ? (
              <Alert type="warning" showIcon message="Pagó, pero la hora que eligió se ocupó mientras pagaba"
                description="Tiene un link en su correo para elegir otra. También puedes darle hora desde aquí." />
            ) : cita.requiereRevision && (
              <Alert type="warning" showIcon message="Requiere revisión" description={cita.motivoRevision} />
            )}

            {/* ── Acciones ── */}
            {puedeEditar && (pagada || e === 'pagando' || boletaPorResolver) && (
              <div className="flex flex-wrap items-center gap-2">
                {pagada && (
                  <button type="button" onClick={() => setReprogramar(true)} disabled={ocupado || Boolean(cita.asistencia)} className={claseBtnPrimario}>
                    <FiCalendar size={15} /> {e === 'pagada_sin_hora' ? 'Elegir hora' : 'Cambiar hora'}
                  </button>
                )}
                {e === 'confirmada' && empezo && !cita.asistencia && (
                  <>
                    <button type="button" onClick={() => accion(() => Agenda.asistencia(cita._id, 'asistio'), 'Asistencia registrada')} disabled={ocupado} className={claseBtnSecundario}>
                      <FiCheck size={15} /> Asistió
                    </button>
                    <button type="button" onClick={() => accion(() => Agenda.asistencia(cita._id, 'no_asistio'), 'Inasistencia registrada')} disabled={ocupado} className={claseBtnSecundario}>
                      <FiSlash size={15} /> No asistió
                    </button>
                  </>
                )}
                {pagada && (
                  <button type="button" onClick={reenviar} disabled={ocupado} className={claseBtnSecundario}>
                    <FiMail size={15} /> Reenviar correo
                  </button>
                )}
                {e === 'pagando' && (
                  <button type="button" onClick={() => accion(() => Agenda.reconciliarPagos(), 'Consulta enviada a Transbank')} disabled={ocupado} className={claseBtnSecundario}>
                    <FiRefreshCw size={15} /> Consultar a Transbank
                  </button>
                )}
                {boletaPorResolver && (
                  <button type="button" onClick={() => setBoleta(b => ({ ...b, abierta: true }))} disabled={ocupado} className={claseBtnSecundario}>
                    <FiFileText size={15} /> Resolver boleta
                  </button>
                )}
                {pagada && !cita.asistencia && (
                  <button type="button" onClick={() => setCancelar(true)} disabled={ocupado} className={claseBtnPeligro}>
                    <FiXCircle size={15} /> Cancelar reserva
                  </button>
                )}
              </div>
            )}
            {e === 'pagando' && (
              <p className="text-xs text-gray-500 m-0">
                Mientras está en pago la hora queda tomada. El sistema le pregunta solo a Transbank cada pocos minutos; el botón lo hace ahora para los pagos de hace más de 15 minutos.
              </p>
            )}

            {/* ── Indicadores, como en la ficha de usuario ── */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
              {[
                { label: 'Duración', value: `${cita.duracionMin} min`, icon: <FiClock size={17} />, ok: true },
                { label: 'Boleta', value: doc?.numero ? `N° ${doc.numero}` : (BOLETA_CORTA[cita.facturacion?.estado] || '—'), icon: <FiFileText size={17} />, ok: Boolean(doc?.numero) },
                { label: 'Cambios de hora', value: cita.reprogramaciones || 0, icon: <FiCalendar size={17} />, ok: cita.reprogramaciones > 0 },
                { label: 'Asistencia', value: cita.asistencia === 'asistio' ? 'Asistió' : cita.asistencia === 'no_asistio' ? 'No asistió' : '—', icon: <FiCheck size={17} />, ok: Boolean(cita.asistencia) },
              ].map(k => (
                <div key={k.label} className="flex items-center gap-3 p-3.5 rounded-2xl bg-white border border-gray-100 min-w-0">
                  <span className={`w-9 h-9 flex-shrink-0 rounded-xl flex items-center justify-center
                    ${k.ok ? 'bg-blue-50 text-primary' : 'bg-gray-100 text-gray-400'}`}>{k.icon}</span>
                  <div className="min-w-0">
                    <p className="text-xl font-extrabold text-gray-900 leading-none m-0 tabular-nums truncate" title={String(k.value)}>{k.value}</p>
                    <p className="text-[11px] font-semibold text-gray-500 m-0 mt-1 leading-tight">{k.label}</p>
                  </div>
                </div>
              ))}
            </div>

            <section className="rounded-3xl bg-white border border-gray-100 px-5 pb-5">
              <Tabs items={[
                {
                  key: 'reserva',
                  label: <Etiqueta icon={<FiCalendar size={15} />} texto="Reserva" />,
                  children: (
                    <>
                      <Dato icon={<FiCalendar size={14} />} label="Hora">
                        {cita.inicio ? <span className="capitalize">{fechaLarga(cita.inicio)}, {hora(cita.inicio)}–{hora(cita.fin)}</span> : 'Sin hora'}
                      </Dato>
                      <Dato icon={<FiUser size={14} />} label="Atiende">{cita.agendaNombre}</Dato>
                      <Dato icon={<FiMapPin size={14} />} label="Lugar">
                        {cita.recintoNombre}
                        {cita.recintoDireccion && <span className="block text-xs text-gray-500">{cita.recintoDireccion}</span>}
                      </Dato>
                      <Dato icon={<FiPhone size={14} />} label="Contacto">
                        <span className="block">{cita.cliente?.rut}</span>
                        <a href={`mailto:${cita.cliente?.email}`} className="block">{cita.cliente?.email}</a>
                        <a href={`tel:${cita.cliente?.telefono}`}>{cita.cliente?.telefono}</a>
                      </Dato>
                      {e === 'cancelada' && (
                        <Dato icon={<FiXCircle size={14} />} label="Cancelación">
                          {cita.motivoCancelacion}
                          <span className="block text-xs text-gray-500">{fechaHora(cita.canceladaEn)}</span>
                          {cita.devolucion?.requerida && (
                            <span className="block mt-1"><Chip tono="rojo">Devolución pendiente para finanzas</Chip> {cita.devolucion.nota}</span>
                          )}
                        </Dato>
                      )}
                    </>
                  ),
                },
                {
                  key: 'pago',
                  label: <Etiqueta icon={<FiCreditCard size={15} />} texto="Pago y boleta" />,
                  children: (
                    <>
                <Dato icon={<FiTag size={14} />} label="Tarifa">
                  <span className="inline-flex flex-wrap items-center gap-2">
                    <span className="font-semibold tabular-nums">{pesos(cita.precio)}</span>
                    {chipDe(TARIFA, cita.tarifa?.tipo || 'normal')}
                    {cita.tarifa?.verificarEnRecinto && <Chip tono="rojo">Verificar cédula en el recinto</Chip>}
                  </span>
                  {cita.tarifa?.precioNormal && cita.tarifa.precioNormal !== cita.precio && (
                    <span className="block text-xs text-gray-500">Precio normal {pesos(cita.tarifa.precioNormal)}</span>
                  )}
                  {cita.tarifa?.vecinoEstado && (
                    <span className="block text-xs text-gray-500">{VECINO_PADRON[cita.tarifa.vecinoEstado] || cita.tarifa.vecinoEstado}</span>
                  )}
                  {cita.cliente?.fechaNacimiento && (
                    <span className="block text-xs text-gray-500">
                      Nacimiento declarado {cita.cliente.fechaNacimiento.split('-').reverse().join('-')}
                      {cita.tarifa?.edad != null ? ` (${cita.tarifa.edad} años)` : ''}
                    </span>
                  )}
                </Dato>
                <Dato icon={<FiCreditCard size={14} />} label="Pago">
                  {RESULTADO_PAGO[cita.pago?.resultado] || (e === 'pagando' ? `En Webpay desde las ${hora(cita.pago?.iniciadoEn)}` : '—')}
                  {cita.pago?.authorizationCode && (
                    <span className="block text-xs text-gray-500">
                      Autorización {cita.pago.authorizationCode} · {TIPO_TARJETA[cita.pago.paymentTypeCode] || cita.pago.paymentTypeCode}
                      {cita.pago.cardNumber ? ` · **** ${cita.pago.cardNumber}` : ''} · {fechaHora(cita.pago.transactionDate)}
                    </span>
                  )}
                  {cita.pago?.buyOrder && <span className="block text-xs text-gray-500 font-mono">Orden {cita.pago.buyOrder}</span>}
                </Dato>
                <Dato icon={<FiFileText size={14} />} label="Boleta">
                  <span className="inline-flex flex-wrap items-center gap-2">
                    {chipDe(BOLETA, cita.facturacion?.estado)}
                    {doc?.numero && (doc.urlPublica || doc.urlPdf
                      ? <a href={doc.urlPublica || doc.urlPdf} target="_blank" rel="noreferrer" className="font-semibold">N° {doc.numero}</a>
                      : <span className="font-semibold">N° {doc.numero}</span>)}
                  </span>
                  {cita.facturacion?.ultimoError && cita.facturacion.estado !== 'emitida' && (
                    <span className="block text-xs text-gray-500 mt-1">{cita.facturacion.ultimoError}</span>
                  )}
                </Dato>
                    </>
                  ),
                },
                {
                  key: 'historial',
                  label: <Etiqueta icon={<FiClock size={15} />} texto="Historial" n={cita.historial?.length} />,
                  children: (
                    <ol className="m-0 p-0 pt-1 list-none space-y-3">
                      {[...(cita.historial || [])].reverse().map((h, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <span className={`mt-1.5 w-2 h-2 flex-shrink-0 rounded-full ${i === 0 ? 'bg-secondary' : 'bg-gray-300'}`} aria-hidden />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 m-0">{ACCIONES_HISTORIAL[h.accion] || h.accion}</p>
                            <p className="text-xs text-gray-500 m-0">
                              {fechaHora(h.fecha)} · {h.por === 'cliente' ? 'la persona' : h.por === 'sistema' ? 'sistema' : 'panel'}
                              {h.detalle?.nueva?.inicio && <> · nueva hora {fechaHora(h.detalle.nueva.inicio)}</>}
                              {h.detalle?.motivo && <> · {h.detalle.motivo}</>}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ol>
                  ),
                },
              ]} />
            </section>
          </>
        )}
      </div>

      <ReprogramarModal
        cita={cita} open={reprogramar} onClose={() => setReprogramar(false)}
        onListo={async () => { setReprogramar(false); await cargar(); onCambio?.(); }}
      />

      <Modal
        open={cancelar} onCancel={() => setCancelar(false)} width={520} destroyOnHidden
        title={<>Cancelar <span className="hl-amarillo">reserva</span></>}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setCancelar(false)} className={claseBtnSecundario}>Volver</button>
            <button type="button" disabled={ocupado || motivo.trim().length < 3} className={claseBtnPrimario}
              onClick={async () => {
                const ok = await accion(() => Agenda.cancelar(cita._id, {
                  motivo: motivo.trim(), devolucionRequerida: devolucion, notaDevolucion: devolucion ? notaDevolucion.trim() : undefined,
                }), 'Reserva cancelada. La persona recibe un correo.');
                if (ok) { setCancelar(false); setMotivo(''); setDevolucion(false); setNotaDevolucion(''); }
              }}>
              {ocupado && <LoadingOutlined />} Cancelar reserva
            </button>
          </div>
        }
      >
        <Alert type="warning" showIcon className="mb-4" message="Cancelar libera la hora, pero no devuelve el pago"
          description="El sistema no emite notas de crédito ni hace reversas en Transbank. Si corresponde devolver el dinero, márcalo aquí y finanzas lo gestiona." />
        <label className="block text-[13px] font-semibold text-gray-700 mb-1">Motivo (lo verá la persona en el correo)</label>
        <Input.TextArea rows={3} value={motivo} onChange={(ev) => setMotivo(ev.target.value)} maxLength={300} showCount />
        <Checkbox className="mt-3" checked={devolucion} onChange={(ev) => setDevolucion(ev.target.checked)}>Corresponde devolución</Checkbox>
        {devolucion && (
          <Input.TextArea className="mt-2" rows={2} placeholder="Nota para finanzas" value={notaDevolucion} onChange={(ev) => setNotaDevolucion(ev.target.value)} />
        )}
      </Modal>

      <Modal
        open={boleta.abierta} onCancel={() => setBoleta(b => ({ ...b, abierta: false }))} width={520} destroyOnHidden
        title={<>Resolver <span className="hl-amarillo">boleta</span></>}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setBoleta(b => ({ ...b, abierta: false }))} className={claseBtnSecundario}>Volver</button>
            <button type="button" disabled={ocupado || (boleta.decision === 'existe' && !boleta.numero)} className={claseBtnPrimario}
              onClick={async () => {
                const ok = await accion(() => Agenda.resolverBoleta(cita._id, {
                  decision: boleta.decision, numero: boleta.numero, urlPublica: boleta.url || undefined,
                }), boleta.decision === 'existe' ? 'Boleta registrada' : 'La boleta vuelve a la cola y se emite en unos minutos');
                if (ok) setBoleta({ abierta: false, decision: 'existe', numero: null, url: '' });
              }}>
              {ocupado && <LoadingOutlined />} Guardar
            </button>
          </div>
        }
      >
        <p className="text-sm text-gray-600">
          Busca en Bsale una boleta a nombre de <strong>{cita?.cliente?.rut}</strong> por <strong>{pesos(cita?.precio)}</strong> con
          el código <code>{cita?.codigo}</code> en el detalle. Emitirla de nuevo si ya existe la duplicaría, y una boleta duplicada solo se anula con nota de crédito.
        </p>
        <Radio.Group value={boleta.decision} onChange={(ev) => setBoleta(b => ({ ...b, decision: ev.target.value }))} className="flex flex-col gap-2">
          <Radio value="existe">La encontré en Bsale</Radio>
          <Radio value="no_existe">No existe: emitirla</Radio>
        </Radio.Group>
        {boleta.decision === 'existe' && (
          <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-3 mt-3">
            <InputNumber placeholder="N° de boleta" min={1} className="w-full" value={boleta.numero} onChange={(v) => setBoleta(b => ({ ...b, numero: v }))} />
            <Input placeholder="Link público (opcional)" value={boleta.url} onChange={(ev) => setBoleta(b => ({ ...b, url: ev.target.value }))} />
          </div>
        )}
      </Modal>
    </Drawer>
  );
}
